import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Skeleton, SkeletonTable } from '../../components/ui/Skeleton';
import { AdvanceBar } from '../../components/shared/AdvanceBar';
import { useProjects } from '../../hooks/useProjects';
import { useAvanceReporte, useAvanceFinanciero, downloadAvanceXlsx, type AvanceReporteDTO } from '../../hooks/useAvance';
import { api } from '../../lib/api-client';
import { toast } from '../../components/ui/Toast';
import { FileText, FileSpreadsheet, PiggyBank, Search, X } from 'lucide-react';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function AvanceDeObraPage() {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingXlsx, setGeneratingXlsx] = useState(false);

  const { data: projectsData, isLoading: loadingProjects } = useProjects({ page: 1, limit: 500 });
  const { data: reporte, isLoading: loadingReporte } = useAvanceReporte(
    selectedProjectId,
    desde || undefined,
    hasta || undefined,
  );
  const { data: financiero } = useAvanceFinanciero(selectedProjectId);

  const selectedProject = (projectsData?.data ?? []).find((p) => p.id === selectedProjectId);
  const projects = projectsData?.data ?? [];
  const suggestions = projects.filter((p) => {
    if (!projectSearch) return true;
    const q = projectSearch.toLowerCase();
    return p.nombre.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q);
  }).slice(0, 8);

  const handleSelectProject = (p: typeof projects[0]) => {
    setSelectedProjectId(p.id);
    setProjectSearch(`${p.codigo} — ${p.nombre}`);
    setShowSuggestions(false);
  };

  const handleClearProject = () => {
    setSelectedProjectId('');
    setProjectSearch('');
    setShowSuggestions(false);
  };

  const handleGeneratePdf = async () => {
    if (!selectedProjectId) return;
    setGeneratingPdf(true);
    try {
      const response = await api.get(`/projects/${selectedProjectId}/avance/pdf`, { responseType: 'blob' });
      const blob = response.data as Blob;
      const magic = await blob.slice(0, 5).text();
      if (magic.startsWith('%PDF')) {
        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const a = document.createElement('a'); a.href = url; a.download = 'reporte-avance.pdf'; a.click(); URL.revokeObjectURL(url);
      } else {
        const url = URL.createObjectURL(new Blob([blob], { type: 'text/html' }));
        window.open(url, '_blank');
      }
      toast('success', 'Reporte generado');
    } catch { toast('error', 'Error al generar reporte'); }
    finally { setGeneratingPdf(false); }
  };


  return (
    <div className="space-y-6 fade-up">
      {/* Header + Project Search */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-brand-dark">Avance de Obra</h1>
        <div className="relative w-full sm:w-96">
          {loadingProjects ? <Skeleton className="h-10 w-full" /> : (
            <>
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
              <input
                type="text"
                className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-9 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                placeholder="Buscar proyecto por nombre o código..."
                value={projectSearch}
                onChange={(e) => {
                  setProjectSearch(e.target.value);
                  setSelectedProjectId('');
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              {selectedProjectId && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onMouseDown={(e) => { e.preventDefault(); handleClearProject(); }}
                >
                  <X size={14} />
                </button>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
                  {suggestions.map((p) => (
                    <button
                      key={p.id}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 flex flex-col border-b border-gray-50 last:border-0"
                      onMouseDown={(e) => { e.preventDefault(); handleSelectProject(p); }}
                    >
                      <span className="text-sm font-medium text-gray-800">{p.nombre}</span>
                      <span className="text-xs text-gray-400">{p.codigo}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!selectedProjectId && (
        <Card><p className="py-8 text-center text-sm text-gray-400">Selecciona un proyecto para ver el reporte de avance</p></Card>
      )}

      {selectedProjectId && (
        <>
          {/* Date Range Filter */}
          <Card className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1"><Input label="Desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
              <div className="flex-1"><Input label="Hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
              <Button variant="secondary" size="sm" onClick={() => { setDesde(''); setHasta(''); }}>Limpiar</Button>
            </div>
          </Card>

          {loadingReporte ? (
            <Card><SkeletonTable rows={6} /></Card>
          ) : reporte ? (
            <ReporteView
              reporte={reporte}
              hasDateFilter={!!(desde || hasta)}
            />
          ) : (
            <Card><p className="py-8 text-center text-sm text-gray-400">No hay datos disponibles para este proyecto</p></Card>
          )}

          {/* Export Buttons */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button variant="primary" onClick={handleGeneratePdf} isLoading={generatingPdf}>
              <FileText size={16} className="mr-2" /> Exportar PDF
            </Button>
            <Button
              variant="secondary"
              isLoading={generatingXlsx}
              onClick={async () => {
                if (!selectedProjectId || !selectedProject) return;
                setGeneratingXlsx(true);
                try {
                  await downloadAvanceXlsx(
                    selectedProjectId,
                    selectedProject.codigo,
                    desde || undefined,
                    hasta || undefined,
                  );
                  toast('success', 'Excel generado');
                } catch { toast('error', 'Error al generar Excel'); }
                finally { setGeneratingXlsx(false); }
              }}
            >
              <FileSpreadsheet size={16} className="mr-2" /> Exportar Excel
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Reporte View Component ──────────────────────────────────────────

function ReporteView({ reporte, hasDateFilter }: { reporte: AvanceReporteDTO; hasDateFilter: boolean }) {
  const hasDescuento = reporte.descuento > 0;
  const descuentoPct = reporte.subtotal_proforma > 0
    ? Math.round(reporte.descuento / reporte.subtotal_proforma * 10000) / 100
    : 0;
  const descuentoPeriodo = reporte.subtotal_proforma > 0
    ? Math.round(reporte.subtotal_periodo * reporte.descuento / reporte.subtotal_proforma * 100) / 100
    : 0;
  const descuentoAcumulado = reporte.subtotal_proforma > 0
    ? Math.round(reporte.subtotal_acumulado * reporte.descuento / reporte.subtotal_proforma * 100) / 100
    : 0;
  const descuentoPendiente = reporte.subtotal_proforma > 0
    ? Math.round(reporte.subtotal_pendiente * reporte.descuento / reporte.subtotal_proforma * 100) / 100
    : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-brand-dark">RESUMEN</h2>
            <p className="text-sm text-gray-600">{reporte.proyecto_nombre}</p>
            <p className="text-xs text-gray-400">{reporte.cliente_nombre}</p>
          </div>
          {(reporte.fecha_desde || reporte.fecha_hasta) && (
            <div className="text-right text-xs text-gray-500">
              {reporte.fecha_desde && <p>Desde: {formatDate(reporte.fecha_desde)}</p>}
              {reporte.fecha_hasta && <p>Hasta: {formatDate(reporte.fecha_hasta)}</p>}
            </div>
          )}
        </div>

        {/* KPI Summary */}
        <div className={`grid gap-4 ${hasDateFilter ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {hasDateFilter && (
            <div className="rounded-lg bg-blue-50 p-4 space-y-2">
              <p className="text-xs font-semibold text-blue-700 uppercase">Avance (Período)</p>
              {hasDescuento && <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal APU</span><span className="font-semibold">{formatCurrency(reporte.subtotal_periodo)}</span></div>}
              {hasDescuento && <div className="flex justify-between text-sm"><span className="text-red-600">Descuento ({descuentoPct}%)</span><span className="font-semibold text-red-600">-{formatCurrency(descuentoPeriodo)}</span></div>}
              <div className="flex justify-between text-sm"><span className="text-gray-600">Total sin IVA</span><span className="font-semibold">{formatCurrency(reporte.total_sin_iva_periodo)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-600">IVA {reporte.iva_rate}%</span><span className="font-semibold">{formatCurrency(reporte.iva_periodo)}</span></div>
              <div className="flex justify-between text-sm border-t border-blue-200 pt-1"><span className="font-bold text-blue-800">TOTAL PROYECTO</span><span className="font-bold text-blue-800">{formatCurrency(reporte.total_periodo)}</span></div>
            </div>
          )}
          <div className="rounded-lg bg-green-50 p-4 space-y-2">
            <p className="text-xs font-semibold text-green-700 uppercase">{hasDateFilter ? 'Acumulado' : 'Total Acumulado del Proyecto'}</p>
            {hasDescuento && <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal APU</span><span className="font-semibold">{formatCurrency(reporte.subtotal_acumulado)}</span></div>}
            {hasDescuento && <div className="flex justify-between text-sm"><span className="text-red-600">Descuento ({descuentoPct}%)</span><span className="font-semibold text-red-600">-{formatCurrency(descuentoAcumulado)}</span></div>}
            <div className="flex justify-between text-sm"><span className="text-gray-600">Total sin IVA</span><span className="font-semibold">{formatCurrency(reporte.total_sin_iva_acumulado)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-600">IVA {reporte.iva_rate}%</span><span className="font-semibold">{formatCurrency(reporte.iva_acumulado)}</span></div>
            <div className="flex justify-between text-sm border-t border-green-200 pt-1"><span className="font-bold text-green-800">TOTAL PROYECTO</span><span className="font-bold text-green-800">{formatCurrency(reporte.total_acumulado)}</span></div>
          </div>
        </div>

        {/* Proforma vs Ejecutado summary (values include IVA) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <div className="rounded-lg bg-indigo-50 p-3 text-center">
            <p className="text-[10px] font-semibold text-indigo-600 uppercase">Proformado</p>
            <p className="text-sm font-bold text-indigo-800">{formatCurrency(reporte.total_proforma)}</p>
          </div>
          <div className="rounded-lg bg-green-50 p-3 text-center">
            <p className="text-[10px] font-semibold text-green-600 uppercase">Ejecutado</p>
            <p className="text-sm font-bold text-green-800">{formatCurrency(reporte.total_acumulado)}</p>
          </div>
          <div className="rounded-lg bg-amber-50 p-3 text-center">
            <p className="text-[10px] font-semibold text-amber-600 uppercase">Pendiente por Ejecutar</p>
            <p className="text-sm font-bold text-amber-800">{formatCurrency(reporte.subtotal_pendiente * (1 + reporte.iva_rate / 100))}</p>
          </div>
          <div className="rounded-lg bg-red-50 p-3 text-center">
            <p className="text-[10px] font-semibold text-red-600 uppercase">Pendiente por Cobrar</p>
            <p className="text-sm font-bold text-red-800">{formatCurrency(reporte.pendiente_por_cobrar)}</p>
          </div>
        </div>

        {/* Progress bars */}
        {reporte.total_proforma > 0 && (
          <div className="mt-4 space-y-2">
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500">Pagos recibidos vs Total proformado</p>
              <AdvanceBar consumed={reporte.total_pagado} total={reporte.total_proforma} mode="progress" />
            </div>
          </div>
        )}

      </Card>

      {/* Detail Table */}
      <Card>
        <h3 className="text-sm font-semibold text-brand-dark mb-3">Detalle por Rubro</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-gray-200 text-left uppercase text-gray-500">
                <th className="px-2 py-2">Med.</th>
                <th className="px-2 py-2 min-w-[160px]">Descripción</th>
                <th className="px-2 py-2 text-right">P. Unit.</th>
                <th className="px-2 py-2 text-right bg-indigo-50">Q Prof.</th>
                <th className="px-2 py-2 text-right bg-indigo-50">$ Prof.</th>
                {hasDateFilter && <th className="px-2 py-2 text-right bg-blue-50">Q Per.</th>}
                {hasDateFilter && <th className="px-2 py-2 text-right bg-blue-50">$ Per.</th>}
                <th className="px-2 py-2 text-right bg-green-50">Q Ejec.</th>
                <th className="px-2 py-2 text-right bg-green-50">$ Ejec.</th>
                <th className="px-2 py-2 text-right bg-amber-50">Q Pend.</th>
                <th className="px-2 py-2 text-right bg-amber-50">$ Pend.</th>
              </tr>
            </thead>
            <tbody>
              {reporte.items.map((item, idx) => {
                const totalProf = item.cantidad_proformada * item.precio_unitario;
                return (
                <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-2 py-1.5 text-gray-500">{item.unidad}</td>
                  <td className="px-2 py-1.5 font-medium text-gray-800">{item.descripcion}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(item.precio_unitario)}</td>
                  <td className="px-2 py-1.5 text-right bg-indigo-50/30">{item.cantidad_proformada > 0 ? item.cantidad_proformada.toFixed(2) : '—'}</td>
                  <td className="px-2 py-1.5 text-right bg-indigo-50/30">{totalProf > 0 ? formatCurrency(totalProf) : '—'}</td>
                  {hasDateFilter && <td className="px-2 py-1.5 text-right bg-blue-50/30">{item.cantidad_periodo > 0 ? item.cantidad_periodo.toFixed(2) : '—'}</td>}
                  {hasDateFilter && <td className="px-2 py-1.5 text-right font-medium bg-blue-50/30">{item.total_periodo > 0 ? formatCurrency(item.total_periodo) : '—'}</td>}
                  <td className="px-2 py-1.5 text-right bg-green-50/30">{item.cantidad_acumulado > 0 ? item.cantidad_acumulado.toFixed(2) : '—'}</td>
                  <td className="px-2 py-1.5 text-right font-medium bg-green-50/30">{item.total_acumulado > 0 ? formatCurrency(item.total_acumulado) : '—'}</td>
                  <td className="px-2 py-1.5 text-right bg-amber-50/30 text-amber-700">{item.cantidad_pendiente > 0 ? item.cantidad_pendiente.toFixed(2) : '—'}</td>
                  <td className="px-2 py-1.5 text-right font-medium bg-amber-50/30 text-amber-700">{item.total_pendiente > 0 ? formatCurrency(item.total_pendiente) : '—'}</td>
                </tr>
                );
              })}

              {/* Adicionales section (includes Servicios Varios merged in) */}
              {reporte.adicionales.length > 0 && (
                <>
                  <tr className="bg-violet-50/60">
                    <td colSpan={hasDateFilter ? 11 : 9} className="px-2 py-2 font-semibold text-violet-800 text-xs uppercase tracking-wide">
                      Adicionales
                    </td>
                  </tr>
                  {reporte.adicionales.map((ad, idx) => {
                    const isSv = !!ad.es_servicio_varios;
                    const valueColor = isSv ? 'text-orange-700' : 'text-violet-700';
                    const rowBg = isSv ? 'bg-orange-50/10' : 'bg-violet-50/10';
                    return (
                      <tr key={`ad-${idx}`} className={`border-b border-gray-50 ${rowBg}`}>
                        <td className="px-2 py-1.5 text-gray-500">{ad.unidad}</td>
                        <td className="px-2 py-1.5 font-medium text-gray-800">{ad.descripcion}</td>
                        <td className={`px-2 py-1.5 text-right ${valueColor}`}>
                          {isSv ? '—' : formatCurrency(ad.precio_unitario)}
                        </td>
                        <td className="px-2 py-1.5 text-right bg-indigo-50/30">—</td>
                        <td className="px-2 py-1.5 text-right bg-indigo-50/30">—</td>
                        {hasDateFilter && <td className="px-2 py-1.5 text-right bg-blue-50/30">{ad.cantidad_periodo > 0 ? ad.cantidad_periodo.toFixed(2) : '—'}</td>}
                        {hasDateFilter && <td className={`px-2 py-1.5 text-right font-medium bg-blue-50/30 ${valueColor}`}>{ad.monto_periodo > 0 ? formatCurrency(ad.monto_periodo) : '—'}</td>}
                        <td className={`px-2 py-1.5 text-right bg-green-50/30 font-medium ${valueColor}`}>{ad.cantidad_acumulado > 0 ? ad.cantidad_acumulado.toFixed(2) : '—'}</td>
                        <td className={`px-2 py-1.5 text-right font-medium bg-green-50/30 ${valueColor}`}>{ad.monto_acumulado > 0 ? formatCurrency(ad.monto_acumulado) : '—'}</td>
                        <td className="px-2 py-1.5 text-right bg-amber-50/30">—</td>
                        <td className="px-2 py-1.5 text-right bg-amber-50/30">—</td>
                      </tr>
                    );
                  })}
                </>
              )}

              {/* Totals row */}
              <tr className="border-t-2 border-gray-300 font-semibold bg-gray-50">
                <td colSpan={3} className="px-2 py-2 text-right text-gray-700">PROFORMADO SIN IVA</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-indigo-800">{formatCurrency(reporte.subtotal_proforma)}</td>
                {hasDateFilter && <td className="px-2 py-2"></td>}
                {hasDateFilter && <td className="px-2 py-2 text-right text-blue-800">{formatCurrency(reporte.subtotal_periodo)}</td>}
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-green-800">{formatCurrency(reporte.subtotal_acumulado)}</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-amber-700">{formatCurrency(reporte.subtotal_pendiente)}</td>
              </tr>
              {hasDescuento && (
                <tr className="font-semibold bg-red-50/30">
                  <td colSpan={3} className="px-2 py-1 text-right text-red-700">DESCUENTO ({descuentoPct}%)</td>
                  <td className="px-2 py-1"></td>
                  <td className="px-2 py-1 text-right text-red-700">-{formatCurrency(reporte.descuento)}</td>
                  {hasDateFilter && <td className="px-2 py-1"></td>}
                  {hasDateFilter && <td className="px-2 py-1 text-right text-red-700">-{formatCurrency(descuentoPeriodo)}</td>}
                  <td className="px-2 py-1"></td>
                  <td className="px-2 py-1 text-right text-red-700">-{formatCurrency(descuentoAcumulado)}</td>
                  <td className="px-2 py-1"></td>
                  <td className="px-2 py-1 text-right text-red-700">-{formatCurrency(descuentoPendiente)}</td>
                </tr>
              )}
              {(reporte.adicionales.length > 0) && (
                <tr className="font-semibold bg-violet-50/30">
                  <td colSpan={3} className="px-2 py-1 text-right text-violet-700">ADICIONALES</td>
                  <td className="px-2 py-1"></td>
                  <td className="px-2 py-1 text-right text-violet-700">—</td>
                  {hasDateFilter && <td className="px-2 py-1"></td>}
                  {hasDateFilter && <td className="px-2 py-1 text-right text-violet-700">{formatCurrency(reporte.adicionales_periodo)}</td>}
                  <td className="px-2 py-1"></td>
                  <td className="px-2 py-1 text-right text-violet-700">{formatCurrency(reporte.adicionales_acumulado)}</td>
                  <td className="px-2 py-1"></td>
                  <td className="px-2 py-1 text-right text-violet-700">—</td>
                </tr>
              )}
              <tr className="border-t border-gray-200 font-semibold bg-gray-50">
                <td colSpan={3} className="px-2 py-2 text-right text-gray-700">TOTAL SIN IVA</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-indigo-800">{formatCurrency(reporte.total_sin_iva_proforma)}</td>
                {hasDateFilter && <td className="px-2 py-2"></td>}
                {hasDateFilter && <td className="px-2 py-2 text-right text-blue-800">{formatCurrency(reporte.total_sin_iva_periodo)}</td>}
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-green-800">{formatCurrency(reporte.total_sin_iva_acumulado)}</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-amber-700">{formatCurrency(reporte.subtotal_pendiente - descuentoPendiente)}</td>
              </tr>
              <tr className="font-semibold bg-gray-50">
                <td colSpan={3} className="px-2 py-1 text-right text-gray-700">IVA {reporte.iva_rate}%</td>
                <td className="px-2 py-1"></td>
                <td className="px-2 py-1 text-right text-indigo-800">{formatCurrency(reporte.total_sin_iva_proforma * reporte.iva_rate / 100)}</td>
                {hasDateFilter && <td className="px-2 py-1"></td>}
                {hasDateFilter && <td className="px-2 py-1 text-right text-blue-800">{formatCurrency(reporte.iva_periodo)}</td>}
                <td className="px-2 py-1"></td>
                <td className="px-2 py-1 text-right text-green-800">{formatCurrency(reporte.iva_acumulado)}</td>
                <td className="px-2 py-1"></td>
                <td className="px-2 py-1 text-right text-amber-700">{formatCurrency(reporte.subtotal_pendiente * reporte.iva_rate / 100)}</td>
              </tr>
              <tr className="font-bold bg-gray-100">
                <td colSpan={3} className="px-2 py-2 text-right text-gray-900">TOTAL PROYECTO</td>
                <td colSpan={2} className="px-2 py-2 text-right text-indigo-900">{formatCurrency(reporte.total_proforma)}</td>
                {hasDateFilter && <td className="px-2 py-2"></td>}
                {hasDateFilter && <td className="px-2 py-2 text-right text-blue-900">{formatCurrency(reporte.total_periodo)}</td>}
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-green-900">{formatCurrency(reporte.total_acumulado)}</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-amber-700">{formatCurrency(reporte.subtotal_pendiente * (1 + reporte.iva_rate / 100))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Payments Section */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <PiggyBank size={18} className="text-blue-600" />
          <h3 className="text-sm font-semibold text-brand-dark">Pagos Recibidos</h3>
        </div>
        {reporte.pagos.length === 0 ? (
          <p className="text-xs text-gray-400">No hay pagos registrados</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2">Notas</th>
                </tr>
              </thead>
              <tbody>
                {reporte.pagos.map((p, idx) => (
                  <tr key={idx} className="border-b border-gray-50">
                    <td className="px-3 py-2">{formatDate(p.fecha)}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(p.monto)}</td>
                    <td className="px-3 py-2 text-gray-500">{p.notas ?? ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 font-semibold">
                  <td className="px-3 py-2">TOTAL PAGADO</td>
                  <td className="px-3 py-2 text-right text-blue-800">{formatCurrency(reporte.total_pagado)}</td>
                  <td></td>
                </tr>
                <tr className="font-semibold">
                  <td className="px-3 py-2 text-gray-600">SALDO PENDIENTE</td>
                  <td className={`px-3 py-2 text-right font-bold ${reporte.saldo_pendiente > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(reporte.saldo_pendiente)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
